import { elementGuidOfHref } from './elementLinks'

/**
 * How the agent reports what it changed at the end of a turn: a fenced code
 * block of this language, one line per affected element, e.g.
 *
 * ```changes
 * - [Außenwand W12](#element:3f2a…): verkürzt von 4,20 m auf 3,80 m; Öffnung hinzugefügt
 * - Decke 00.DS01: Flächenlast 2,0 kN/m² aufgebracht
 * - Projekt: Windlast-Einstellungen geändert
 * ```
 *
 * The chat renders it as the "Vorgenommene Änderungen" card (`ChatPre`).
 */
export const CHANGE_SUMMARY_LANGUAGE = 'changes'

/** The entries shown before the card is expanded. */
export const COLLAPSED_CHANGE_ENTRIES = 5

export interface ChangeSummaryEntry {
  /** The element (or other subject) the line is about; null for a line without one. */
  subject: string | null
  /** The element's guid when the agent linked it. */
  guid: string | null
  changes: string[]
}

const LIST_MARKER = /^\s*(?:[-*•]|\d+[.)])\s+/
const LINKED_SUBJECT = /^\[([^\]]+)\]\(([^)\s]+)\)\s*:?\s*(.*)$/
const PLAIN_SUBJECT = /^([^:]{1,120}):\s*(.+)$/

const splitChanges = (text: string): string[] =>
  text
    .split(';')
    .map(change => change.trim())
    .filter(change => change !== '')

/** The entries of a `changes` block, one per non-empty line. */
export const parseChangeSummary = (text: string): ChangeSummaryEntry[] =>
  text.split('\n').flatMap((rawLine): ChangeSummaryEntry[] => {
    const line = rawLine.replace(LIST_MARKER, '').trim()
    if (line === '') return []

    const linked = LINKED_SUBJECT.exec(line)
    if (linked) {
      return [
        {
          subject: linked[1].trim(),
          guid: elementGuidOfHref(linked[2]),
          changes: splitChanges(linked[3]),
        },
      ]
    }
    const plain = PLAIN_SUBJECT.exec(line)
    if (plain) return [{ subject: plain[1].trim(), guid: null, changes: splitChanges(plain[2]) }]
    return [{ subject: null, guid: null, changes: splitChanges(line) }]
  })

/** The text of a `changes` code block a `pre` element holds, or null for any other block. */
export const changeSummaryTextOfPre = (node: unknown): string | null => {
  const code = (node as HastElement | undefined)?.children?.find(
    child => child.type === 'element' && child.tagName === 'code',
  )
  const className = code?.properties?.className
  const classes = Array.isArray(className) ? className : [className]
  if (!code || !classes.includes(`language-${CHANGE_SUMMARY_LANGUAGE}`)) return null
  return (code.children ?? []).map(child => child.value ?? '').join('')
}

interface HastElement {
  type: string
  tagName?: string
  value?: string
  properties?: { className?: unknown }
  children?: HastElement[]
}
